import type { JSX } from 'react'

import { useMemo } from 'react'

import type { TimelineEvent } from '@/components/ui/vertical-event-timeline'
import type { LeaveLifecycleStepDto } from '@/utils/api/leave-service/models'

import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { VerticalEventTimeline } from '@/components/ui/vertical-event-timeline'
import { useTrackLeaveApplication } from '@/utils/api/leave-service'
import { LeaveRequestStatus } from '@/utils/api/leave-service/models'
import { formatDate, formatDateTime } from '@/utils/DateHelpers'

interface TrackingDialogProps {
  leaveRequestId: number | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function TrackingDialog({ leaveRequestId, open, onOpenChange }: TrackingDialogProps) {
  const trackingQuery = useTrackLeaveApplication(leaveRequestId!, {
    query: { enabled: !!leaveRequestId && open },
  })

  const tracking = trackingQuery.data?.data
  const startDateLabel = tracking?.startDate ? formatDate(new Date(tracking.startDate)) : '—'
  const endDateLabel = tracking?.endDate ? formatDate(new Date(tracking.endDate)) : '—'
  const currentStatus = tracking?.currentStatus
  const currentStatusLabel = currentStatus ? currentStatus.replaceAll('_', ' ') : '—'
  const currentStatusVariant = currentStatus === LeaveRequestStatus.CANCELLED
    || currentStatus === LeaveRequestStatus.REJECTED
    || currentStatus === LeaveRequestStatus.CEO_REJECTED
    ? 'destructive'
    : currentStatus === LeaveRequestStatus.CEO_APPROVED
      ? 'success'
      : currentStatus?.includes('RECOMMEND') || currentStatus === LeaveRequestStatus.HR_ACKNOWLEDGEMENT
        ? 'warning'
        : currentStatus === LeaveRequestStatus.SUBMITTED
          ? 'info'
          : currentStatus === LeaveRequestStatus.DRAFT
            ? 'secondary'
            : 'outline'

  // Convert tracking steps to timeline events
  const timelineEvents: TimelineEvent[] = useMemo(() => {
    const approvalSteps = tracking?.approvalSteps ?? []
    return approvalSteps.map((step: LeaveLifecycleStepDto, index: number) => {
      const firstIncompleteStep = approvalSteps.find(s => !s.isCompleted && !s.isSkipped)
      const isCurrentStep = firstIncompleteStep?.stepNumber === step.stepNumber
      return {
        id: step.stepNumber ?? index,
        title: step.roleTitle ?? `Step ${step.stepNumber ?? index + 1}`,
        role: step.roleTitle,
        approver: step.expectedApproverName,
        action: step.action,
        timestamp: step.actionTimestamp ? formatDateTime(step.actionTimestamp) : undefined,
        isCompleted: step.isCompleted ?? false,
        isSkipped: step.isSkipped ?? false,
        isCurrent: isCurrentStep,
        description: step.comments
          ? step.comments
          : step.roleTitle
            ? `${step.roleTitle} - ${step.expectedApproverName ?? 'No approver assigned'}`
            : undefined,
      }
    })
  }, [tracking?.approvalSteps])

  let dialogBody: JSX.Element
  if (trackingQuery.isLoading) {
    dialogBody = (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    )
  }
  else if (trackingQuery.isError) {
    dialogBody = (
      <Alert variant="destructive">
        <AlertDescription>
          Failed to load tracking information. Please try again.
        </AlertDescription>
      </Alert>
    )
  }
  else {
    dialogBody = (
      <div className="space-y-6">
        {/* Request Summary */}

        <Label className="text-lg font-semibold">Request Summary</Label>

        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase text-muted-foreground">Leave Type</Label>
              <p className="font-medium mt-1">{tracking?.leaveType}</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase text-muted-foreground">Current Status</Label>
              <Badge variant={currentStatusVariant} className="mt-1">{currentStatusLabel}</Badge>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase text-muted-foreground">Duration</Label>
              <p className="font-medium mt-1">
                {tracking?.workingDays}
                {' '}
                days
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase text-muted-foreground">Start Date</Label>
              <p className="font-medium mt-1">
                {startDateLabel}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase text-muted-foreground">End Date</Label>
              <p className="font-medium mt-1">
                {endDateLabel}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {/* Approval Timeline */}
          {timelineEvents.length > 0 && (
            <>
              <hr className="my-4 border-t-4 border-gray-200" />
              <Label className="text-lg font-semibold">Approval Timeline</Label>
              <VerticalEventTimeline events={timelineEvents} />
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Leave Request Tracking</DialogTitle>
          <DialogDescription>
            View the approval timeline and status of this leave request
          </DialogDescription>
        </DialogHeader>

        {dialogBody}

      </DialogContent>
    </Dialog>
  )
}
